/** @ts-check */

const MOBILE_FILTERS_SELECTOR = '[data-mobile-filters]';
/**
 * Type guards
 * @param {unknown} el
 * @returns {el is HTMLElement}
 */
const isHTMLElement = (el) => el instanceof HTMLElement;

/**
 * @param {unknown} el
 * @returns {el is HTMLButtonElement}
 */
const isHTMLButton = (el) => el instanceof HTMLButtonElement;

/**
 * @param {unknown} el
 * @returns {el is HTMLInputElement}
 */
const isHTMLInput = (el) => el instanceof HTMLInputElement;

/**
 * DOM helpers (typed)
 * @template {Element} T
 * @param {ParentNode} root
 * @param {string} selector
 * @returns {T | null}
 */
const qs = (root, selector) => /** @type {T | null} */(root.querySelector(selector));

/**
 * @template {Element} T
 * @param {ParentNode} root
 * @param {string} selector
 * @returns {NodeListOf<T>}
 */
const qsa = (root, selector) => /** @type {NodeListOf<T>} */(root.querySelectorAll(selector));

const toggleClearBtn = (drawer) => {
  const clearBtn = qs(drawer, '[data-mobile-filters-clear]');
  if (!isHTMLButton(clearBtn)) return;

  // ✅ checkbox selected?
  const anyChecked = qs(drawer, '.mobile-filters__checkbox:checked');

  // ✅ price filter active?
  const hasPriceFilter = (() => {
    const range = qs(drawer, '[data-price-range]');
    if (!isHTMLElement(range)) return false;

    const minDefault = Number(range.dataset.rangeMin || 0);
    const maxDefault = Number(range.dataset.rangeMax || 0);

    const minInput = qs(range, '[data-price-min-input]');
    const maxInput = qs(range, '[data-price-max-input]');

    if (!isHTMLInput(minInput) || !isHTMLInput(maxInput)) return false;

    const minVal = minInput.value ? Number(minInput.value) : minDefault;
    const maxVal = maxInput.value ? Number(maxInput.value) : maxDefault;

    return minVal !== minDefault || maxVal !== maxDefault;
  })();

  // ✅ FINAL CONDITION
// 🔥 URL params bhi check karo
const urlParams = new URLSearchParams(window.location.search);

// Shopify filters usually "filter." se start hote hain
const hasActiveFiltersInURL = Array.from(urlParams.keys()).some((key) =>
  key.startsWith('filter.')
);

clearBtn.disabled = !(anyChecked || hasPriceFilter || hasActiveFiltersInURL);
};

/**
 * @typedef {HTMLElement & {
 *  closeDialog?: () => void;
 *  openDialog?: () => void;
 * }} DialogLike
 */

/**
 * Horizon facets component (custom element)
 * @typedef {Element & {
 *  updateFilters?: () => void;
 *  updateFiltersByURL?: (url: string) => void;
 *  createURLParameters?: (formData?: FormData) => URLSearchParams;
 * }} FacetsFormComponentLike
 */

/**
 * @param {HTMLElement} drawer
 * @param {string} tab
 * @returns {void}
 */
const setActiveTab = (drawer, tab) => {
  drawer.dataset.activeTab = tab;

  const tabs = qsa(drawer, '[data-mobile-filters-tab]');
  tabs.forEach(
    /** @param {Element} button */
    (button) => {
      if (!isHTMLButton(button)) return;
      const isActive = (button.dataset.mobileFiltersTab || '') === tab;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
  );

  const filtersView = qs(drawer, '.mobile-filters__filters-view');
  const sortView = qs(drawer, '.mobile-filters__sort-view');

  if (isHTMLElement(filtersView)) {
    filtersView.toggleAttribute('hidden', tab === 'sort');
  }
  if (isHTMLElement(sortView)) {
    sortView.toggleAttribute('hidden', tab !== 'sort');
  }
};

/**
 * @param {HTMLElement} drawer
 * @param {number} index
 * @returns {void}
 */
const setActiveFacet = (drawer, index) => {
  const buttons = qsa(drawer, '[data-facet-index]');
  const panels = qsa(drawer, '[data-facet-panel]');

  buttons.forEach(
    /** @param {Element} button */
    (button) => {
      if (!isHTMLButton(button)) return;
      const isActive = (button.dataset.facetIndex || '') === index.toString();
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
  );

  panels.forEach(
    /** @param {Element} panel */
    (panel) => {
      if (!isHTMLElement(panel)) return;
      const isActive = (panel.dataset.facetPanel || '') === index.toString();
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
    }
  );
};

/**
 * @param {HTMLElement} drawer
 * @returns {number}
 */
const getDefaultFacetIndex = (drawer) => {
  const buttons = Array.from(qsa(drawer, '[data-facet-index]'));

  if (!buttons[0]) return 0;

  const activeButton = buttons.find(
    /** @param {Element} button */
    (button) => button.getAttribute('data-has-active') === 'true'
  );

  const target = activeButton ? activeButton : buttons[0];

  const attr = target.getAttribute('data-facet-index');
  return Number(attr ?? 0);
};

/**
 * @param {HTMLInputElement} input
 * @returns {void}
 */
const filterOptions = (input) => {
  const panel = input.closest('.mobile-filters__facet-panel');
  if (!panel) return;

  const query = input.value.trim().toLowerCase();
  const items = qsa(panel, '[data-value-label]');

  items.forEach(
    /** @param {Element} item */
    (item) => {
      if (!isHTMLElement(item)) return;
      const label = (item.dataset.valueLabel || '').toLowerCase();
      const shouldShow = query === '' || label.includes(query);
      item.toggleAttribute('hidden', !shouldShow);
    }
  );
};

/**
 * @param {HTMLElement} drawer
 * @returns {void}
 */
const resetSearchInputs = (drawer) => {
  const inputs = qsa(drawer, '[data-facet-search]');
  inputs.forEach(
    /** @param {Element} input */
    (input) => {
      if (!isHTMLInput(input)) return;
      input.value = '';
      filterOptions(input);
    }
  );
};

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
const clampValue = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * @param {HTMLElement} container
 * @param {number} minValue
 * @param {number} maxValue
 * @returns {void}
 */
const updateRangeTrack = (container, minValue, maxValue) => {
  const track = qs(container, '[data-range-track]');
  if (!isHTMLElement(track)) return;

  const min = Number(container.dataset.rangeMin || 0);
  const max = Number(container.dataset.rangeMax || 0);
  const range = max - min || 1;

  const minPercent = ((minValue - min) / range) * 100;
  const maxPercent = ((maxValue - min) / range) * 100;

  track.style.setProperty('--range-min', `${minPercent}%`);
  track.style.setProperty('--range-max', `${maxPercent}%`);
};

/**
 * @param {number} value
 * @returns {string}
 */
const formatCount = (value) => new Intl.NumberFormat().format(value);

/**
 * @param {HTMLButtonElement} applyButton
 * @param {number} count
 * @returns {void}
 */
const updateApplyButtonLabel = (applyButton, count) => {
  const label = applyButton.dataset.mobileFiltersApplyLabel || 'Show';
  const countValue = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  applyButton.dataset.mobileFiltersApplyCount = String(countValue);

  // const textTarget = qs(applyButton, '[data-mobile-filters-apply-text]');
  // const text = `${label} (${formatCount(countValue)})`;
  // if (isHTMLElement(textTarget)) {
  //   textTarget.textContent = text;
  // } else {
  //   applyButton.textContent = text;
  // }
};

/**
 * @param {string} value
 * @returns {number}
 */
const parseNumericInputValue = (value) => {
  const parsed = Number((value || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * @param {HTMLElement} container
 * @returns {void}
 */
const initPriceRange = (container) => {
  if (container.dataset.priceRangeInit === 'true') return;
  container.dataset.priceRangeInit = 'true';

  const minRange = qs(container, '[data-range-min]');
  const maxRange = qs(container, '[data-range-max]');
  const minInput = qs(container, '[data-price-min-input]');
  const maxInput = qs(container, '[data-price-max-input]');
  const toggle = qs(container, '[data-price-toggle]');
  const panel = qs(container, '.mobile-filters__price-panel');

  if (!isHTMLInput(minRange) || !isHTMLInput(maxRange) || !isHTMLInput(minInput) || !isHTMLInput(maxInput)) {
    return;
  }

  updateRangeTrack(container, Number(minRange.value), Number(maxRange.value));

  /**
   * @param {"min" | "max"} source
   * @returns {void}
   */
  const syncFromRange = (source) => {
    let minVal = Number(minRange.value);
    let maxVal = Number(maxRange.value);

    if (minVal > maxVal) {
      if (source === 'min') {
        minVal = maxVal;
        minRange.value = String(minVal);
      } else {
        maxVal = minVal;
        maxRange.value = String(maxVal);
      }
    }

    minInput.value = String(minVal);
    maxInput.value = String(maxVal);
    updateRangeTrack(container, minVal, maxVal);
  };

  /**
   * @param {"min" | "max"} source
   * @returns {void}
   */
  const syncFromInputs = (source) => {
    const min = Number(container.dataset.rangeMin || 0);
    const max = Number(container.dataset.rangeMax || 0);

    /** @param {string} value */
    const sanitize = (value) => value.replace(/[^0-9.]/g, '');

    if (source === 'min') {
      const rawValue = sanitize(minInput.value);
      if (rawValue === '') {
        minRange.value = String(min);
        updateRangeTrack(container, Number(minRange.value), Number(maxRange.value));
        return;
      }
      let value = clampValue(Number(rawValue), min, max);
      const maxVal = Number(maxRange.value);
      if (value > maxVal) value = maxVal;
      minInput.value = String(value);
      minRange.value = String(value);
    } else {
      const rawValue = sanitize(maxInput.value);
      if (rawValue === '') {
        maxRange.value = String(max);
        updateRangeTrack(container, Number(minRange.value), Number(maxRange.value));
        return;
      }
      let value = clampValue(Number(rawValue), min, max);
      const minVal = Number(minRange.value);
      if (value < minVal) value = minVal;
      maxInput.value = String(value);
      maxRange.value = String(value);
    }

    updateRangeTrack(container, Number(minRange.value), Number(maxRange.value));
  };

  minRange.addEventListener('input', () => syncFromRange('min'));
  maxRange.addEventListener('input', () => syncFromRange('max'));

  minInput.addEventListener('input', () => syncFromInputs('min'));
  maxInput.addEventListener('input', () => syncFromInputs('max'));
  minInput.addEventListener('change', () => syncFromInputs('min'));
  maxInput.addEventListener('change', () => syncFromInputs('max'));

  if (isHTMLButton(toggle)) {
    toggle.addEventListener('click', () => {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      if (isHTMLElement(panel)) panel.toggleAttribute('hidden', isExpanded);
    });
  }
};

/**
 * @param {HTMLElement} drawer
 * @returns {void}
 */
const initMobileFilters = (drawer) => {
  /** @type {DialogLike} */
  const dialogComponent = /** @type {any} */ (drawer);

  /** @type {FacetsFormComponentLike | null} */
  const formComponent = /** @type {any} */ (qs(drawer, 'facets-form-component'));

  const applyButton = qs(drawer, '[data-mobile-filters-apply]');
  const clearButton = qs(drawer, '[data-mobile-filters-clear]');
  const form = qs(drawer, '.facets__form');
  const tabButtons = qsa(drawer, '[data-mobile-filters-tab]');
  const facetButtons = qsa(drawer, '[data-facet-index]');
  /** @type {AbortController | null} */
  let countUpdateAbortController = null;
  let lastPreviewQuery = '';

  /**
   * @param {HTMLElement} panel
   * @returns {number}
   */
  const getFacetPanelSelectionCount = (panel) => {
    const checkboxes = qsa(panel, ".mobile-filters__checkbox[type='checkbox']");
    if (checkboxes.length > 0) {
      return Array.from(checkboxes).filter((input) => isHTMLInput(input) && input.checked).length;
    }

    const minInput = qs(panel, '[data-price-min-input]');
    const maxInput = qs(panel, '[data-price-max-input]');
    const rangeContainer = qs(panel, '[data-price-range]');

    if (
      !isHTMLInput(minInput) ||
      !isHTMLInput(maxInput) ||
      !isHTMLElement(rangeContainer)
    ) {
      return 0;
    }

    const minDefault = Number(rangeContainer.dataset.rangeMin || 0);
    const maxDefault = Number(rangeContainer.dataset.rangeMax || 0);
    const minValue = minInput.value.trim() === '' ? minDefault : parseNumericInputValue(minInput.value);
    const maxValue = maxInput.value.trim() === '' ? maxDefault : parseNumericInputValue(maxInput.value);

    return minValue !== minDefault || maxValue !== maxDefault ? 1 : 0;
  };

  /**
   * @returns {void}
   */
  const updateFacetItemCounts = () => {
    const buttons = qsa(drawer, '[data-facet-index]');

    buttons.forEach(
      /** @param {Element} button */
      (button) => {
        if (!isHTMLButton(button)) return;
        const index = button.dataset.facetIndex || '0';
        const panel = qs(drawer, `[data-facet-panel="${index}"]`);
        if (!isHTMLElement(panel)) return;

        const count = getFacetPanelSelectionCount(panel);
        const countNode = qs(button, '[data-facet-count]');
        if (!isHTMLElement(countNode)) return;

        countNode.textContent = String(count);
        countNode.classList.toggle('hidden', count === 0);
        countNode.toggleAttribute('aria-hidden', count === 0);
      }
    );
  };

  /**
   * Fetches filtered section HTML and updates the Show (N) count without re-rendering the grid.
   * @returns {Promise<void>}
   */
  const syncPreviewCount = async () => {
    if (window.__typesenseActive) return;
    if (!(form instanceof HTMLFormElement)) return;
    if (!isHTMLButton(applyButton)) return;
    const dialog = qs(drawer, 'dialog');
    if (dialog instanceof HTMLDialogElement && !dialog.open) return;

    const sectionId = formComponent?.getAttribute('section-id');
    if (!sectionId) return;

    /** @type {URLSearchParams} */
    let params;
    if (formComponent && typeof formComponent.createURLParameters === 'function') {
      params = formComponent.createURLParameters(new FormData(form));
    } else {
      params = new URLSearchParams(/** @type {any} */(new FormData(form)));
      params.delete('page');
    }

    const url = new URL(window.location.href);
    url.search = '';
    for (const [key, value] of params.entries()) {
      url.searchParams.append(key, value);
    }

    const nextPreviewQuery = url.searchParams.toString();
    if (nextPreviewQuery === lastPreviewQuery) return;
    lastPreviewQuery = nextPreviewQuery;

    url.searchParams.set('section_id', sectionId);

    if (countUpdateAbortController) countUpdateAbortController.abort();
    countUpdateAbortController = new AbortController();

    try {
      const response = await fetch(url.toString(), { signal: countUpdateAbortController.signal });
      if (!response.ok) return;
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const parsedApplyButton = qs(parsed, '[data-mobile-filters-apply]');
      if (!isHTMLButton(parsedApplyButton)) return;
      const count = Number(parsedApplyButton.dataset.mobileFiltersApplyCount || 0);
      updateApplyButtonLabel(applyButton, count);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
    }
  };

  /** @type {number} */
  let previewCountTimeoutId = 0;

  /**
   * @returns {void}
   */
  const schedulePreviewCountSync = () => {
    window.clearTimeout(previewCountTimeoutId);
    previewCountTimeoutId = window.setTimeout(() => {
      syncPreviewCount();
    }, 220);
  };

  tabButtons.forEach(
    /** @param {Element} button */
    (button) => {
      if (!isHTMLButton(button)) return;
      button.addEventListener('click', () => {
        setActiveTab(drawer, button.dataset.mobileFiltersTab ?? 'filters');
      });
    }
  );

  facetButtons.forEach(
    /** @param {Element} button */
    (button) => {
      if (!isHTMLButton(button)) return;
      button.addEventListener('click', () => {
        const index = Number(button.dataset.facetIndex ?? 0);
        setActiveFacet(drawer, index);
      });
    }
  );

  qsa(drawer, '[data-facet-search]').forEach(
    /** @param {Element} input */
    (input) => {
      if (!isHTMLInput(input)) return;
      input.addEventListener('input', () => filterOptions(input));
    }
  );

  qsa(drawer, '[data-price-range]').forEach(
    /** @param {Element} container */
    (container) => {
      if (isHTMLElement(container)) initPriceRange(container);
    }
  );

  if (isHTMLButton(applyButton)) {
    const initialCount = Number(applyButton.dataset.mobileFiltersApplyCount || 0);
    updateApplyButtonLabel(applyButton, initialCount);

    applyButton.addEventListener('click', () => {
      // In Typesense mode, typesense-collection.js handles filter application
      if (!window.__typesenseActive) {
        if (formComponent && typeof formComponent.updateFilters === 'function') {
          formComponent.updateFilters();
        }
      }
      if (typeof dialogComponent.closeDialog === 'function') {
        dialogComponent.closeDialog();
      }
    });
  }

  if (isHTMLButton(clearButton)) {
    clearButton.addEventListener('click', () => {
      // In Typesense mode, typesense-collection.js handles clear (shows loader, clears URL, reinits)
      if (window.__typesenseActive) return;

      const resultsUrl = drawer.dataset.resultsUrl ?? '';

      resetSearchInputs(drawer);

      qsa(drawer, "input[type='checkbox']").forEach(
        /** @param {Element} input */
        (input) => {
          if (isHTMLInput(input)) input.checked = false;
        }
      );

      qsa(drawer, '[data-price-range]').forEach(
        /** @param {Element} container */
        (container) => {
          if (!isHTMLElement(container)) return;

          const min = container.dataset.rangeMin || '0';
          const max = container.dataset.rangeMax || '0';

          const minRange = qs(container, '[data-range-min]');
          const maxRange = qs(container, '[data-range-max]');
          const minInput = qs(container, '[data-price-min-input]');
          const maxInput = qs(container, '[data-price-max-input]');

          if (isHTMLInput(minRange)) minRange.value = min;
          if (isHTMLInput(maxRange)) maxRange.value = max;
          if (isHTMLInput(minInput)) minInput.value = min;
          if (isHTMLInput(maxInput)) maxInput.value = max;

          updateRangeTrack(container, Number(min), Number(max));
        }
      );

      if (formComponent && typeof formComponent.updateFiltersByURL === 'function' && resultsUrl) {
        formComponent.updateFiltersByURL(resultsUrl);
        // 🔥 URL bhi clean karo
        history.replaceState({}, '', resultsUrl);
      }

      updateFacetItemCounts();
      if (isHTMLButton(applyButton)) {
        const resetCount = Number(applyButton.dataset.mobileFiltersApplyCount || 0);
        updateApplyButtonLabel(applyButton, resetCount);
      }

      // ✅ better timing without setTimeout
      // 1st immediate update
      toggleClearBtn(drawer);

      // 2nd after DOM update (Shopify re-render case)
      setTimeout(() => {
        toggleClearBtn(drawer);
      }, 50);

      // if (typeof dialogComponent.closeDialog === 'function') {
      //   dialogComponent.closeDialog();
      // }
    });
  }

  dialogComponent.addEventListener('dialog:open', () => {
    setActiveTab(drawer, 'filters');
    setActiveFacet(drawer, getDefaultFacetIndex(drawer));
    updateFacetItemCounts();
    requestAnimationFrame(() => toggleClearBtn(drawer));

    qsa(drawer, '[data-price-range]').forEach(
      /** @param {Element} container */
      (container) => {
        if (!isHTMLElement(container)) return;
        const minRange = qs(container, '[data-range-min]');
        const maxRange = qs(container, '[data-range-max]');
        if (!isHTMLInput(minRange) || !isHTMLInput(maxRange)) return;
        updateRangeTrack(container, Number(minRange.value), Number(maxRange.value));
      }
    );

    if (isHTMLButton(applyButton)) {
      const initialCount = Number(applyButton.dataset.mobileFiltersApplyCount || 0);
      updateApplyButtonLabel(applyButton, initialCount);
    }
  });

  if (form instanceof HTMLFormElement) {
    form.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.matches('[data-facet-search]')) return;
      updateFacetItemCounts();
      toggleClearBtn(drawer);
      schedulePreviewCountSync();
    });
    form.addEventListener('input', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.matches('[data-facet-search]')) return;
      updateFacetItemCounts();
      toggleClearBtn(drawer);

      schedulePreviewCountSync();
    });
  }

  updateFacetItemCounts();
  toggleClearBtn(drawer);
};

document.addEventListener('DOMContentLoaded', () => {
  qsa(document, MOBILE_FILTERS_SELECTOR).forEach(
    /** @param {Element} drawer */
    (drawer) => {
      if (isHTMLElement(drawer)) initMobileFilters(drawer);
    }
  );

  qsa(document, '[data-price-range]').forEach(
    /** @param {Element} container */
    (container) => {
      if (isHTMLElement(container)) initPriceRange(container);
    }
  );
});

document.addEventListener('shopify:section:load', () => {
  document.querySelectorAll('[data-mobile-filters]').forEach((drawer) => {
    toggleClearBtn(drawer);
  });
});